// Cross-cutting registry that lets tools running inside ONE chat
// read state from EVERY chat open in the same browser window.
// Populated by ChatConsole on mount with closures over its
// chatsRef and agentsRef; every tool gets at the data through this
// indirection so we don't have to thread chatsRef into every
// factory.
//
// Why a singleton: the audit toolkit (and any future cross-chat
// agent — meta-analysis, regression detection, etc.) needs a stable
// point of access that doesn't change when React re-mounts. A
// closure registered at app start is enough; we never need more
// than one ChatConsole rendered at a time.
//
// Lifetime: cleared via setChatRegistry(null) when the console
// unmounts. Tools that fire after unmount get empty results, never
// stale snapshots.

let _getChats = () => [];
let _getAgents = () => new Map();

// Install (or clear) the registry. Pass `null` to clear.
export function setChatRegistry(handlers) {
    if (handlers == null) {
        _getChats = () => [];
        _getAgents = () => new Map();
        return;
    }
    if (typeof handlers.getChats === "function") _getChats = handlers.getChats;
    if (typeof handlers.getAgents === "function") _getAgents = handlers.getAgents;
}

export function resetChatRegistryIdentityState() {
    setChatRegistry(null);
}

// Compact summary of every open chat — enough for an auditor agent
// to decide which chat to dig into without paying for the full
// transcript. Mirrors the per-chat block in the chats-bundle export
// but trimmed to the fields a triage agent actually needs.
export function listChatSummaries() {
    const chats = _getChats() || [];
    return chats.map((c) => {
        const items = Array.isArray(c.items) ? c.items : [];
        const firstUser = items.find((i) => i?.kind === "user");
        const lastUser = [...items].reverse().find((i) => i?.kind === "user");
        const turns = items.filter((i) => i?.kind === "turn");
        return {
            chat_id: c.id,
            title: c.title || "",
            item_count: items.length,
            turn_count: turns.length,
            busy: !!c.busy,
            error: c.error || null,
            first_user_prompt: firstUser?.text || null,
            last_user_prompt: lastUser?.text || null,
            thinking_level: c.thinkingLevel || null,
            scope_mode: c.scopeMode || null,
        };
    });
}

// Full chat record (or null). Tools should call this and pass the
// returned `chat` into the lineage walker / transcript renderer
// rather than re-reading from the registry, so a single tool call
// works against a stable snapshot.
export function getChatById(id) {
    const chats = _getChats() || [];
    const key = String(id);
    return chats.find((c) => String(c.id) === key) || null;
}

// Pi-agent-core Agent instance for a chat. Used by read_chat_transcript
// when the caller asks for the LLM-facing view (which lives on
// agent.state.messages, not chat.items).
export function getAgentById(id) {
    const map = _getAgents() || new Map();
    // agentsRef keys can be numeric or string-coerced depending on
    // how the chat was instantiated; try both.
    if (map.has(id)) return map.get(id) || null;
    const n = Number(id);
    if (!Number.isNaN(n) && map.has(n)) return map.get(n) || null;
    return map.get(String(id)) || null;
}

// The open chat whose persistent id is `serverChatId`, or null.
export function getChatByServerId(serverChatId) {
    if (serverChatId == null || serverChatId === "") return null;
    const key = String(serverChatId);
    return (_getChats() || []).find((c) => c?.serverChatId != null && String(c.serverChatId) === key) || null;
}
